// ─────────────────────────────────────────────────────────────────────────
// AusenciasCard — vacaciones y permisos de un trabajador (27-ago-2026).
// Componente standalone: recibe `trabajadorId` y se conecta solo al backend
// (/ausencia). Reglas legales (validadas por el abogado):
//   - Vacaciones: 15 días hábiles/año, devengo 1,25 días/mes desde el
//     ingreso. El sábado NO es hábil para vacaciones.
//   - Art. 66 CT: conviven días corridos y hábiles → la unidad se elige
//     SIEMPRE a mano por registro (nunca hay default silencioso).
//   - Fuero de 1 mes por muerte de hijo o cónyuge → campo informativo.
// Los días los calcula el servidor; acá solo se muestran.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import backend from "../../backend";

const TIPO = {
  vacaciones:          { label: "Vacaciones",        color: "#3498db", bg: "#0d2030" },
  permiso_con_goce:    { label: "Permiso con goce",  color: "#2ecc71", bg: "#0f2a1a" },
  permiso_sin_goce:    { label: "Permiso sin goce",  color: "#95a5a6", bg: "#222626" },
  licencia_medica:     { label: "Licencia médica",   color: "#e67e22", bg: "#2d1f0d" },
  licencia_mutual:     { label: "Licencia por accidente laboral (mutual)", color: "#e74c3c", bg: "#2d1515" },
  permiso_legal_art66: { label: "Permiso legal art. 66", color: "#9b59b6", bg: "#241530" },
  falta:               { label: "Falta (inasistencia)", color: "#f39c12", bg: "#2d230d" },
};
// Título del documento imprimible según el tipo de registro
const TITULO_COMPROBANTE = {
  vacaciones: "Comprobante de feriado legal",
  permiso_con_goce: "Comprobante de permiso con goce de sueldo",
  permiso_sin_goce: "Comprobante de permiso sin goce de sueldo",
  licencia_medica: "Registro de licencia médica",
  licencia_mutual: "Registro de licencia por accidente laboral",
  permiso_legal_art66: "Comprobante de permiso legal (art. 66 del Código del Trabajo)",
  falta: "Registro de inasistencia",
};
const TIPOS_CON_CAUSAL = ["permiso_legal_art66", "licencia_mutual"];
const ESTADO = {
  solicitada: { label: "Solicitada", color: "#f39c12" },
  aprobada:   { label: "Aprobada",   color: "#2ecc71" },
  rechazada:  { label: "Rechazada",  color: "#e74c3c" },
};

const num = { fontVariantNumeric: "tabular-nums" };
const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4 },
  input: { background: "#161616", border: "1px solid #333", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#ddd", width: "100%", boxSizing: "border-box" },
  grid:  { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 },
  chip:  (color, bg) => ({ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color, background: bg, border: `1px solid ${color}33`, whiteSpace: "nowrap" }),
  btnMini: (color, borde, bg) => ({ minHeight: 38, borderRadius: 8, border: `1px solid ${borde}`, background: bg, color, fontSize: 12, fontWeight: 700, cursor: "pointer", padding: "0 12px" }),
};

const fmtFecha = v => (v ? new Date(v).toLocaleDateString("es-CL", { timeZone: "UTC" }) : "—");
const fmtDias = n => {
  const v = Math.round((Number(n) || 0) * 100) / 100;
  return Number.isInteger(v) ? String(v) : v.toFixed(2).replace(".", ",");
};

const FORM_VACIO = {
  tipo: "vacaciones", unidadDias: "habiles", fechaInicio: "", fechaFin: "",
  motivoLegal: "", fueroHasta: "", comentario: "",
};

// Comprobante de vacaciones (28-sep-2026, pedido del contador): documento
// imprimible para la firma del trabajador, a partir de una ausencia de tipo
// vacaciones ya aprobada. Se arma en el navegador; no guarda nada nuevo.
const escHtml = s => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const fechaLarga = v => (v ? new Date(v).toLocaleDateString("es-CL", { timeZone: "UTC", day: "numeric", month: "long", year: "numeric" }) : "—");

// Saldo de vacaciones A LA FECHA DE TÉRMINO del registro (no el de hoy), con la
// misma fórmula del backend (GET /ausencia/saldo): arrastre inicial + 1,25 ×
// meses completos desde el ingreso hasta `fechaFin` − vacaciones aprobadas cuyo
// término es hasta ese registro inclusive. Así reimprimir un comprobante antiguo
// no resta lo tomado después. Devuelve null si falta la fecha de ingreso.
const mesesCompletos = (desde, hasta) => {
  let meses = (hasta.getUTCFullYear() - desde.getUTCFullYear()) * 12 + (hasta.getUTCMonth() - desde.getUTCMonth());
  if (hasta.getUTCDate() < desde.getUTCDate()) meses -= 1;
  return Math.max(0, meses);
};
const saldoAlCierre = (a, saldo, rows, trabajador) => {
  if (!saldo || !saldo.fechaIngreso || !a?.fechaFin) return null;
  let corte = new Date(a.fechaFin);
  if (trabajador?.estado === "desvinculado" && trabajador.fechaDesvinculacion) {
    const d = new Date(trabajador.fechaDesvinculacion);
    if (d < corte) corte = d;
  }
  const devengados = (Number(saldo.ajusteSaldoInicial) || 0) + mesesCompletos(new Date(saldo.fechaIngreso), corte) * 1.25;
  const fin = new Date(a.fechaFin).getTime();
  const tomados = (rows || [])
    .filter(r => r.tipo === "vacaciones" && r.estado === "aprobada" && new Date(r.fechaFin).getTime() <= fin)
    .reduce((t, r) => t + (Number(r.dias) || 0), 0);
  return Math.round((devengados - tomados) * 100) / 100;
};

const imprimirComprobante = (a, trabajador, saldo, rows) => {
  const w = window.open("", "_blank");
  if (!w) return false;
  const nombre = `${trabajador?.nombres || ""} ${trabajador?.apellidos || ""}`.trim();
  const saldoDespues = a.tipo === "vacaciones" ? saldoAlCierre(a, saldo, rows, trabajador) : null;
  const fila = (k, v) => `<tr><td class="k">${escHtml(k)}</td><td>${escHtml(v)}</td></tr>`;
  const titulo = TITULO_COMPROBANTE[a.tipo] || "Comprobante";
  const unidad = a.unidadDias === "corridos" ? "corridos" : "hábiles";
  const declaracion = {
    vacaciones: "El trabajador declara hacer uso de su feriado legal en las fechas indicadas, conforme al artículo 67 del Código del Trabajo, y recibir copia de este comprobante.",
    permiso_con_goce: "El empleador autoriza el permiso en las fechas indicadas, con goce de remuneración. El trabajador declara recibir copia de este comprobante.",
    permiso_sin_goce: "El empleador autoriza el permiso en las fechas indicadas, sin goce de remuneración, por lo que esos días se descuentan de la remuneración del mes. El trabajador declara recibir copia de este comprobante.",
    permiso_legal_art66: "El trabajador hace uso del permiso legal por la causal indicada, conforme al artículo 66 del Código del Trabajo, y declara recibir copia de este comprobante.",
    licencia_medica: "Se deja constancia de la licencia médica presentada por el trabajador para las fechas indicadas.",
    licencia_mutual: "Se deja constancia de la licencia otorgada por el organismo administrador de la Ley 16.744 para las fechas indicadas, por la causa señalada.",
    falta: "Se deja constancia de la inasistencia del trabajador en las fechas indicadas, sin justificación presentada a la fecha de emisión.",
  }[a.tipo] || "";
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>${escHtml(titulo)} — ${escHtml(nombre)}</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 40px; font-size: 13px; }
  h1 { font-size: 18px; margin: 0 0 2px; }
  .sub { color: #555; margin-bottom: 22px; }
  table { border-collapse: collapse; width: 100%; margin-bottom: 18px; }
  td { border: 1px solid #bbb; padding: 7px 10px; vertical-align: top; }
  td.k { width: 38%; background: #f1f3f6; font-weight: bold; }
  h2 { font-size: 13px; margin: 18px 0 6px; text-transform: uppercase; letter-spacing: .04em; }
  .firmas { display: flex; gap: 60px; margin-top: 90px; }
  .firma { flex: 1; border-top: 1px solid #111; padding-top: 6px; text-align: center; }
  .nota { color: #555; font-size: 11px; margin-top: 26px; }
  @media print { body { margin: 20mm; } }
</style></head><body>
<h1>${escHtml(titulo)}</h1>
<div class="sub">Binimeliz S.A. · RUT 96.911.420-8 · Emitido el ${escHtml(fechaLarga(new Date()))}</div>
<h2>Trabajador</h2>
<table>
  ${fila("Nombre", nombre || "—")}
  ${fila("RUT", trabajador?.rut || "—")}
  ${fila("Cargo", trabajador?.cargo || "—")}
  ${fila("Fecha de ingreso", fechaLarga(trabajador?.fechaIngreso))}
</table>
<h2>Detalle</h2>
<table>
  ${fila("Desde", fechaLarga(a.fechaInicio))}
  ${fila("Hasta (inclusive)", fechaLarga(a.fechaFin))}
  ${fila(`Días ${unidad}`, fmtDias(a.dias))}
  ${a.motivoLegal ? fila("Causa", a.motivoLegal) : ""}
  ${a.tipo === "vacaciones" && saldoDespues != null ? fila("Saldo de vacaciones después de este período", `${fmtDias(saldoDespues)} días hábiles`) : ""}
  ${a.comentario ? fila("Observaciones", a.comentario) : ""}
</table>
<p>${escHtml(declaracion)}</p>
<div class="firmas">
  <div class="firma">${escHtml(nombre || "Trabajador")}<br>Firma del trabajador</div>
  <div class="firma">Binimeliz S.A.<br>Firma del empleador</div>
</div>
<div class="nota">${a.unidadDias === "corridos" ? "Los días se cuentan corridos, incluidos sábados, domingos y festivos." : "Los días se cuentan de lunes a viernes; el sábado es inhábil."} Una vez firmado, este documento se escanea y se guarda en los documentos del trabajador.</div>
<script>window.onload = function () { window.print(); };</script>
</body></html>`);
  w.document.close();
  return true;
};

const AusenciasCard = ({ trabajadorId, trabajador }) => {
  const [saldo, setSaldo] = useState(null);
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState(null);
  const [abierto, setAbierto] = useState(false); // formulario nueva ausencia
  const [f, setF] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);

  const avisar = (tipo, texto) => { setAviso({ tipo, texto }); setTimeout(() => setAviso(null), 5000); };
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const cargar = useCallback(async () => {
    if (!trabajadorId) return;
    setCargando(true);
    try {
      const [rSaldo, rLista] = await Promise.all([
        backend.get(`ausencia/saldo/${trabajadorId}`),
        backend.get(`ausencia?trabajador=${trabajadorId}`),
      ]);
      setSaldo(rSaldo.data);
      setRows(rLista.data || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  }, [trabajadorId]);
  useEffect(() => { cargar(); }, [cargar]);

  const cambiarTipo = tipo => {
    // Vacaciones son SIEMPRE hábiles; para el resto la unidad se elige a mano.
    setF(prev => ({ ...prev, tipo, unidadDias: tipo === "vacaciones" ? "habiles" : prev.unidadDias }));
  };

  const guardar = async () => {
    if (!f.fechaInicio || !f.fechaFin) { avisar("error", "Faltan las fechas"); return; }
    setGuardando(true);
    try {
      await backend.post("ausencia", {
        trabajador: trabajadorId,
        tipo: f.tipo,
        unidadDias: f.unidadDias,
        fechaInicio: f.fechaInicio,
        fechaFin: f.fechaFin,
        motivoLegal: TIPOS_CON_CAUSAL.includes(f.tipo) ? f.motivoLegal.trim() : "",
        fueroHasta: f.tipo === "permiso_legal_art66" ? (f.fueroHasta || null) : null,
        comentario: f.comentario.trim(),
      });
      avisar("ok", "Solicitud registrada");
      setF(FORM_VACIO);
      setAbierto(false);
      cargar();
    } catch (e) { avisar("error", e?.response?.data?.error || e.message); }
    setGuardando(false);
  };

  const resolver = async (a, accion) => {
    try {
      await backend.patch(`ausencia/${a._id}/${accion}`, {});
      avisar("ok", accion === "aprobar" ? "Registro aprobado" : "Registro rechazado");
      cargar();
    } catch (e) { avisar("error", e?.response?.data?.error || e.message); }
  };

  const eliminar = async a => {
    if (!window.confirm("¿Eliminar esta solicitud pendiente?")) return;
    try {
      await backend.delete(`ausencia/${a._id}`);
      avisar("ok", "Solicitud eliminada");
      cargar();
    } catch (e) { avisar("error", e?.response?.data?.error || e.message); }
  };

  if (!trabajadorId) return null;

  return (
    <div style={S.card}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>🏖 Vacaciones, permisos, licencias y faltas</div>
        {aviso && (
          <span style={{ fontSize: 12, fontWeight: 700, color: aviso.tipo === "ok" ? "#2ecc71" : "#e74c3c" }}>
            {aviso.tipo === "ok" ? "✓ " : "⚠ "}{aviso.texto}
          </span>
        )}
      </div>

      {/* ── Saldo de vacaciones ── */}
      <div style={{ display: "flex", gap: 18, alignItems: "baseline", flexWrap: "wrap", margin: "12px 0" }}>
        <div>
          <div style={S.label}>Saldo de vacaciones</div>
          <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1, ...num, color: saldo && saldo.saldo < 0 ? "#e74c3c" : "#fff" }}>
            {saldo ? fmtDias(saldo.saldo) : "…"}
            <span style={{ fontSize: 13, fontWeight: 600, color: "#8a8a8a" }}> días hábiles</span>
          </div>
        </div>
        {saldo && (
          <div style={{ fontSize: 11, color: "#8a8a8a", ...num }}>
            Devengados: <span style={{ color: "#b8b8b8" }}>{fmtDias(saldo.devengados)}</span>
            {" · "}Tomados: <span style={{ color: "#b8b8b8" }}>{fmtDias(saldo.tomados)}</span>
            {saldo.ajusteSaldoInicial ? <>{" · "}Arrastre inicial: <span style={{ color: "#b8b8b8" }}>{fmtDias(saldo.ajusteSaldoInicial)}</span></> : null}
            <div style={{ marginTop: 2 }}>
              Devengo 1,25 días/mes desde el ingreso{saldo.fechaIngreso ? ` (${fmtFecha(saldo.fechaIngreso)})` : " — ⚠ ficha sin fecha de ingreso"} · sábado no es hábil
            </div>
          </div>
        )}
      </div>

      {/* ── Nueva ausencia ── */}
      {!abierto ? (
        <button onClick={() => setAbierto(true)} className="t-02 tap-card"
          style={{ minHeight: 40, borderRadius: 10, border: "none", background: "#e30613", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "0 16px", marginBottom: 12 }}>
          + Registrar vacaciones, permiso, licencia o falta
        </button>
      ) : (
        <div style={{ background: "#1c1c1c", border: "1px solid #2e2e2e", borderRadius: 10, padding: 12, marginBottom: 12 }}>
          <div style={S.grid}>
            <div>
              <div style={S.label}>Tipo</div>
              <select style={S.input} value={f.tipo} onChange={e => cambiarTipo(e.target.value)}>
                {Object.entries(TIPO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <div style={S.label}>Unidad de los días</div>
              <select style={S.input} value={f.unidadDias} onChange={e => set("unidadDias", e.target.value)} disabled={f.tipo === "vacaciones"}>
                <option value="habiles">Hábiles (L-V)</option>
                <option value="corridos">Corridos (incluye finde)</option>
              </select>
            </div>
            <div>
              <div style={S.label}>Desde</div>
              <input type="date" style={S.input} value={f.fechaInicio} onChange={e => set("fechaInicio", e.target.value)} />
            </div>
            <div>
              <div style={S.label}>Hasta (inclusive)</div>
              <input type="date" style={S.input} value={f.fechaFin} onChange={e => set("fechaFin", e.target.value)} />
            </div>
          </div>

          <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 8 }}>
            {f.tipo === "vacaciones"
              ? "Vacaciones se cuentan SIEMPRE en días hábiles: lunes a viernes, el sábado no es hábil."
              : f.tipo === "licencia_mutual"
              ? "Licencia del organismo de la Ley 16.744 (mutual, ACHS o ISL) por accidente del trabajo, accidente de trayecto o enfermedad profesional. Anote la causa: queda como registro de accidentabilidad."
              : f.tipo === "falta"
              ? "Inasistencia sin justificar. Se descuenta de la remuneración del mes."
              : f.tipo === "permiso_legal_art66"
              ? "El art. 66 CT mezcla ambas unidades según la causal: ej. 10 días CORRIDOS por muerte de un hijo, 4 días HÁBILES por muerte de padre o madre. Elija según la causal exacta."
              : "Hábiles = solo lunes a viernes. Corridos = todos los días del rango, fin de semana incluido."}
            {" "}Los días los calcula el sistema (v1 sin tabla de feriados: un feriado L-V se cuenta como hábil).
          </div>

          {f.tipo === "licencia_mutual" && (
            <div style={{ marginTop: 10 }}>
              <div style={S.label}>Causa</div>
              <input style={S.input} value={f.motivoLegal} onChange={e => set("motivoLegal", e.target.value)}
                placeholder="accidente del trabajo, accidente de trayecto o enfermedad profesional" />
            </div>
          )}

          {f.tipo === "permiso_legal_art66" && (
            <div style={{ ...S.grid, marginTop: 10 }}>
              <div>
                <div style={S.label}>Causal legal</div>
                <input style={S.input} value={f.motivoLegal} onChange={e => set("motivoLegal", e.target.value)}
                  placeholder="ej. muerte de padre, nacimiento de hijo…" />
              </div>
              <div>
                <div style={S.label}>Fuero hasta (informativo)</div>
                <input type="date" style={S.input} value={f.fueroHasta} onChange={e => set("fueroHasta", e.target.value)} />
                <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 3 }}>
                  Muerte de hijo o cónyuge da fuero laboral de 1 mes — anote acá hasta cuándo rige.
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 10 }}>
            <div style={S.label}>Comentario</div>
            <input style={S.input} value={f.comentario} onChange={e => set("comentario", e.target.value)} placeholder="Opcional" />
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={guardar} disabled={guardando} className="t-02"
              style={{ minHeight: 40, borderRadius: 10, border: "none", background: guardando ? "#333" : "#e30613", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "0 18px" }}>
              {guardando ? "Guardando…" : "Registrar solicitud"}
            </button>
            <button onClick={() => { setAbierto(false); setF(FORM_VACIO); }} className="t-02"
              style={{ minHeight: 40, borderRadius: 10, border: "1px solid #333", background: "#1e1e1e", color: "#b8b8b8", fontSize: 12, cursor: "pointer", padding: "0 14px" }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── Lista ── */}
      {cargando ? (
        <div style={{ color: "#8a8a8a", fontSize: 12 }}>Cargando…</div>
      ) : rows.length === 0 ? (
        <div style={{ color: "#8a8a8a", fontSize: 12 }}>Sin registros.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map(a => {
            const t = TIPO[a.tipo] || TIPO.permiso_con_goce;
            const e = ESTADO[a.estado] || ESTADO.solicitada;
            return (
              <div key={a._id} style={{ background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 10, padding: "10px 12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={S.chip(t.color, t.bg)}>{t.label}</span>
                  <span style={{ fontSize: 10, fontWeight: 700, color: e.color }}>● {e.label}</span>
                  <span style={{ fontSize: 12, color: "#ddd", ...num }}>
                    {fmtFecha(a.fechaInicio)} → {fmtFecha(a.fechaFin)}
                  </span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: "#fff", ...num }}>
                    {fmtDias(a.dias)} {a.unidadDias === "corridos" ? "corridos" : "hábiles"}
                  </span>
                  {a.estado === "aprobada" && (
                    <span style={{ marginLeft: "auto" }}>
                      <button className="t-02" style={S.btnMini("#3498db", "#1d3a52", "#0d2030")}
                        onClick={() => { if (!imprimirComprobante(a, trabajador, saldo, rows)) avisar("error", "El navegador bloqueó la ventana del comprobante"); }}>
                        Comprobante
                      </button>
                    </span>
                  )}
                  {a.estado === "solicitada" && (
                    <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      <button onClick={() => resolver(a, "aprobar")} className="t-02" style={S.btnMini("#2ecc71", "#1d4a2e", "#10261a")}>Aprobar</button>
                      <button onClick={() => resolver(a, "rechazar")} className="t-02" style={S.btnMini("#e74c3c", "#4a2020", "#2d1515")}>Rechazar</button>
                      <button onClick={() => eliminar(a)} className="t-02" style={S.btnMini("#8a8a8a", "#333", "#1e1e1e")}>✕</button>
                    </span>
                  )}
                </div>
                {(a.motivoLegal || a.fueroHasta || a.comentario) && (
                  <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 4 }}>
                    {a.motivoLegal && <span>Causal: {a.motivoLegal}. </span>}
                    {a.fueroHasta && <span style={{ color: "#9b59b6", fontWeight: 700 }}>Fuero hasta {fmtFecha(a.fueroHasta)}. </span>}
                    {a.comentario && <span>{a.comentario}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AusenciasCard;
